import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Upload } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { errMsg } from "./ResolveDialog";

export function BrandColorField({ value, onChange, disabled }) {
  return (
    <div>
      <Label>Brand colour</Label>
      <div className="flex items-center gap-2">
        <input type="color" value={/^#[0-9a-f]{6}$/i.test(value) ? value : "#18181b"} onChange={(e) => onChange(e.target.value)}
          disabled={disabled} className="h-9 w-12 cursor-pointer rounded border border-zinc-300 bg-white p-0.5 disabled:cursor-not-allowed"
          aria-label="Pick brand colour" data-testid="settings-brand-color-picker" />
        <Input value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} maxLength={7}
          className="w-28 font-mono-num" data-testid="settings-brand-color" />
        <span className="text-xs text-zinc-500">Email header line, buttons and client pages</span>
      </div>
    </div>
  );
}

export function LogoField({ logoUrl, onChange, isLead }) {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  async function call(fn, ok) {
    setBusy(true);
    try { onChange((await fn()).data); toast.success(ok); }
    catch (e) { toast.error("Logo update failed", { description: errMsg(e) }); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  }
  const upload = (file) => {
    const fd = new FormData();
    fd.append("file", file);
    call(() => api.post("/checkin-settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } }), "Logo uploaded");
  };
  return (
    <div>
      <Label>Clinic logo</Label>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex h-14 min-w-[120px] items-center justify-center rounded border border-dashed border-zinc-300 bg-white px-3">
          {logoUrl ? <img src={logoUrl} alt="Clinic logo" className="max-h-12 max-w-[200px] object-contain" data-testid="settings-logo-img" />
            : <span className="text-xs text-zinc-400" data-testid="settings-logo-empty">No logo</span>}
        </div>
        {isLead && (
          <>
            <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden"
              onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid="settings-logo-input" />
            <Button size="sm" variant="outline" disabled={busy} onClick={() => ref.current?.click()} data-testid="settings-logo-upload">
              {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-1.5 h-3.5 w-3.5" />} {logoUrl ? "Replace" : "Upload"}
            </Button>
            {logoUrl && (
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => call(() => api.delete("/checkin-settings/logo"), "Logo removed")} data-testid="settings-logo-remove">
                <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Remove
              </Button>
            )}
          </>
        )}
      </div>
      <p className="mt-1 text-xs text-zinc-500">PNG, JPG, WebP or GIF under 2 MB. Shown at the top of progress review emails and client pages.</p>
    </div>
  );
}
